import React, { useEffect, useState } from 'react';
import {
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Card,
    CardContent
} from '@material-ui/core';
import { SelectInput } from 'components';
import { Close } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    formField: {
        marginBottom: theme.spacing(2),
        width: '70px'
    },
    fieldLabel: {
        minWidth: '150px',
        textAlign: 'left'
    },
    saveButton: {
        marginRight: '16px',
        marginBottom: '12px'
    }
}));

const DateModal = props => {
    const { isOpen, onClose, onSave, initialValues } = props;
    const classes = useStyles();
    const [dateOption, setDateOption] = useState({
        dynamicDate: false,
        dynamicField: 'customDate',
        dateFormatType: 'Day',
        dateOffset: 0,
        staticDateField: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        if (initialValues) {
             let updatedValues = {
            ...initialValues,
            dynamicField: initialValues.dynamicDate === false ? 'customDate' : initialValues.dynamicField
        };
        if (updatedValues.staticDateField) {
            const parsed = moment(updatedValues.staticDateField, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
            if (parsed.isValid()) {
                updatedValues.staticDateField = parsed.format('YYYY-MM-DD');
            }
        }
        setDateOption(updatedValues);
        } else {
            setDateOption({
                dynamicDate: false,
                dynamicField: 'customDate',
                dateFormatType: 'Day',
                dateOffset: 0,
                staticDateField: new Date().toISOString().split('T')[0]
            });
        }
    }, [initialValues, isOpen]);

    const dateOptions = {
        list: [
            { id: 'thisDay', value: 'thisDay', label: 'ThisDay' },
            { id: 'thisMonth', value: 'thisMonth', label: 'ThisMonth' },
            { id: 'thisYear', value: 'thisYear', label: 'ThisYear' },
            { id: 'today', value: 'today', label: 'Today' },
            { id: 'weekFromNow', value: 'weekFromNow', label: 'WeekFromNow' },
            { id: 'customDate', value: 'customDate', label: 'Custom Date' }
        ],
        keyId: 'id',
        valueId: 'value',
        displayValueId: 'label'
    };

    const getAllFormatOptions = () => [
        { id: 'Day', value: 'Day', label: 'Day' },
        { id: 'Month', value: 'Month', label: 'Month' },
        { id: 'Year', value: 'Year', label: 'Year' },
        { id: 'DayMonth', value: 'DayMonth', label: 'DayMonth' },
        { id: 'MonthYear', value: 'MonthYear', label: 'MonthYear' },
        { id: 'DayMonthYear', value: 'DayMonthYear', label: 'DayMonthYear' }
    ];

    const getFilteredFormatOptions = () => {
        const allOptions = getAllFormatOptions();
        switch (dateOption.dynamicField) {
            case 'thisDay':
                return allOptions.filter(option => option.value === 'Day');
            case 'thisMonth':
                return allOptions.filter(option => option.value === 'Month');
            case 'thisYear':
                return allOptions.filter(option => option.value === 'Year');
            case 'today':
            case 'weekFromNow':
            case 'customDate':
                return allOptions;
            default:
                return allOptions;
        }
    };

    const formatOptions = {
        list: getFilteredFormatOptions(),
        keyId: 'id',
        valueId: 'value',
        displayValueId: 'label'
    };

    const handleDateOptionChange = (event) => {
        const newSelectedOption = event.target.value;

        setDateOption(prev => ({
            ...prev,
            dynamicField: newSelectedOption,
            dynamicDate: newSelectedOption !== 'customDate',
            dateOffset: 0,
            staticDateField: newSelectedOption === 'customDate' ? new Date().toISOString().split('T')[0] : null
        }));

        switch (newSelectedOption) {
            case 'thisDay':
                setDateOption(prev => ({ ...prev, dateFormatType: 'Day' }));
                break;
            case 'thisMonth':
                setDateOption(prev => ({ ...prev, dateFormatType: 'Month' }));
                break;
            case 'thisYear':
                setDateOption(prev => ({ ...prev, dateFormatType: 'Year' }));
                break;
            default:
                setDateOption(prev => ({ ...prev, dateFormatType: 'Day' }));
                break;
        }
    };

    const handleFormatChange = (event) => {
        setDateOption(prev => ({ ...prev, dateFormatType: event.target.value }));
    };

    const handleOffsetChange = (event) => {
        const value = parseInt(event.target.value) || 0;
        setDateOption(prev => ({ ...prev, dateOffset: value, staticDateField: null }));
    };

    const handleCustomDateChange = (date) => {
        // date is a Moment object
        const formattedDate = date ? date.format('YYYY-MM-DD') : null;
        setDateOption(prev => ({ ...prev, staticDateField: formattedDate }));
    };

    const onClickSave = () => {
        if (onSave) {
            const payload = {
            ...dateOption,
            dynamicField: dateOption.dynamicField === 'customDate' ? null : dateOption.dynamicField
        };
            onSave(payload);
        }
        onClose();
    }

    const getHelpText = () => {
        const { dynamicField, dateOffset, dateFormatType, staticDateField } = dateOption;

        const formatTypeMap = {
            'Day': 'DD',
            'Month': 'MM',
            'Year': 'YYYY',
            'DayMonth': 'DD-MM',
            'MonthYear': 'MM-YYYY',
            'DayMonthYear': 'YYYY-MM-DD'
        };
        const formatPart = formatTypeMap[dateFormatType] || 'DD';

        const getOffsetText = (unit, current) => {
            if (dateOffset === 0) {
                return <>
                    <div>Rule applies when current {current} matches the date parameter.</div>
                    <div>Example: "current {current} + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === 1) {
                return <>
                    <div>Rule applies when next {current} matches the date parameter.</div>
                    <div>Example: "current {current} + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === -1) {
                return <>
                    <div>Rule applies when previous {current} matches the date parameter.</div>
                    <div>Example: "current {current} + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset > 1) {
                return <>
                    <div>Rule applies when {dateOffset} {unit} from current {current} matches the date parameter.</div>
                    <div>Example: "current {current} + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset < -1) {
                return <>
                    <div>Rule applies when {Math.abs(dateOffset)} {unit} before current {current} matches the date parameter.</div>
                    <div>Example: "current {current} + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            }
        };

        const getTodayOffsetText = () => {
            if (dateOffset === 0) {
                return <>
                    <div>Rule applies when current date ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current date + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === 1) {
                return <>
                    <div>Rule applies when next date ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current date + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === -1) {
                return <>
                    <div>Rule applies when previous date ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current date + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset > 1) {
                return <>
                    <div>Rule applies when {dateOffset} days from current date ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current date + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset < -1) {
                return <>
                    <div>Rule applies when {Math.abs(dateOffset)} days before current date ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current date + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            }
        };

        const getWeekOffsetText = () => {
            if (dateOffset === 0) {
                return <>
                <div>Rule applies when current week ({formatPart}) matches the date parameter.</div>
                <div>Example: "current week + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === 1) {
                return <>
                    <div>Rule applies when next week ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current week + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset === -1) {
                return <>
                    <div>Rule applies when previous week ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current week + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset > 1) {
                return <>
                    <div>Rule applies when {dateOffset} weeks from current week ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current week + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            } else if (dateOffset < -1) {
                return <>
                    <div>Rule applies when {Math.abs(dateOffset)} weeks before current week ({formatPart}) matches the date parameter.</div>
                    <div>Example: "current week + offset" formatted as "{formatPart}" matches (2025-08-25).</div>
                </>;
            }
        };

        const getCustomDateText = () => {
            return <>
                <div>Rule applies when selected custom date ({formatPart}) matches the date parameter.</div>
                <div>Example: "custom date" formatted as "{formatPart}" matches (2025-08-25).</div>
            </>;
        };

        switch (dynamicField) {
            case 'thisDay':
                return getOffsetText('days', 'date');
            case 'thisMonth':
                return getOffsetText('months', 'month');
            case 'thisYear':
                return getOffsetText('years', 'year');
            case 'today':
                return getTodayOffsetText();
            case 'weekFromNow':
                return getWeekOffsetText();
            case 'customDate':
                return getCustomDateText();
            default:
                return null;
        }
    }

    return (
        <Dialog
            open={isOpen}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="sm">
            <Typography variant="h2">
                <DialogTitle id="form-dialog-title" disableTypography={true}>
                    Set Date Condition
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}>
                    <Close />
                </IconButton>
            </Typography>
            <DialogContent>
                <Card>
                    <CardContent>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" className={classes.fieldLabel}>
                                Date Type:
                            </Typography>
                            <SelectInput
                                customStyles={{ width: '170px', marginBottom: '14px' }}
                                label="Select Date Option"
                                margin="dense"
                                name={dateOption.dynamicField}
                                onChange={handleDateOptionChange}
                                value={dateOption.dynamicField || 'customDate'}
                                options={dateOptions}
                            />
                            {dateOption.dynamicField !== 'customDate' && (
                                <TextField
                                    className={classes.formField}
                                    label="Offset"
                                    margin="dense"
                                    type="number"
                                    value={dateOption.dateOffset ?? 0}
                                    onChange={handleOffsetChange}
                                    variant="outlined"
                                    style={{ marginLeft: '16px' }}
                                />
                            )}
                            {dateOption.dynamicField === 'customDate' && (
                                <MuiPickersUtilsProvider utils={MomentUtils} >
                                    <KeyboardDatePicker
                                        margin="dense"
                                        label="Date"
                                        format="YYYY-MM-DD"
                                        value={dateOption.staticDateField ? moment(dateOption.staticDateField, 'YYYY-MM-DD') : null}
                                        onChange={handleCustomDateChange}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date'
                                        }}
                                        variant="inline"
                                        inputVariant="outlined"
                                        style={{ marginLeft: '16px', marginTop: '0px', width: '170px' }}
                                    />
                                </MuiPickersUtilsProvider>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" className={classes.fieldLabel}>
                            </Typography>
                            <div>
                                <SelectInput
                                    fullWidth
                                    customStyles={{ width: '170px' }}
                                    label="Date Format Style"
                                    margin="dense"
                                    name="formatOptions"
                                    onChange={handleFormatChange}
                                    value={dateOption.dateFormatType || 'Day'}
                                    options={formatOptions}
                                    disabled={
                                        dateOption.dynamicField === 'thisDay' ||
                                        dateOption.dynamicField === 'thisMonth' ||
                                        dateOption.dynamicField === 'thisYear'
                                    }
                                />
                            </div>
                        </div>
                        <i style={{ color: '#666' }}>{getHelpText()}</i>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    className={classes.saveButton}
                    color="primary"
                    variant="contained"
                    onClick={onClickSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default DateModal;